import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Bot, Send, X, Sparkles, Loader2, Minimize2, Maximize2, ChevronLeft, Save, Copy, Check, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import ReactMarkdown from 'react-markdown';

interface Msg {
  role: 'user' | 'assistant';
  content: string;
  hidden?: boolean;
}

type AgentMode = 'office' | 'field' | 'portal';

interface PersonalAgentChatProps {
  mode?: AgentMode;
  companyName?: string;
  extraHeaders?: Record<string, string>;
  onBack?: () => void;
  onClose?: () => void;
  embedded?: boolean;
  /** Compact mode for split-panel */
  compact?: boolean;
  /** Auto-send this message on mount */
  initialMessage?: string;
  /** Callback when user wants to save AI response as call note */
  onSaveNote?: (text: string) => void;
  /** Whether a save-note action is available */
  showSaveNote?: boolean;
}

// SECURITY (L10): chat history (which can include business/customer data
// discussed with the AI assistant) was cached in localStorage indefinitely.
// Store a savedAt timestamp alongside the messages and discard anything
// older than CHAT_HISTORY_MAX_AGE_MS on load, instead of keeping it forever.
const CHAT_HISTORY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function loadStoredMessages(key: string): Msg[] {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    // Backward-compatible with the old format (a bare array, no timestamp) --
    // treat existing untimestamped history as already-expired so it's cleared
    // on first load under the new scheme rather than kept forever.
    if (Array.isArray(parsed)) return [];
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.messages)) {
      if (typeof parsed.savedAt === 'number' && Date.now() - parsed.savedAt <= CHAT_HISTORY_MAX_AGE_MS) {
        return parsed.messages;
      }
    }
    return [];
  } catch {
    return [];
  }
}

const QUICK_SUGGESTIONS: Record<AgentMode, string[]> = {
  office: [
    'Giv mig en status-oversigt',
    'Opret en opgave',
    'Opret en ny sag',
    'Find en kunde',
    'Find en faktura',
    'Hvad er status på en sag?',
  ],
  field: [
    'Hvad er mine opgaver i dag?',
    'Hvornår er min næste vagt?',
    'Hjælp mig med at rapportere',
    'Har jeg ubesvaret beskeder?',
  ],
  portal: [
    'Hvad er status på min sag?',
    'Hvornår starter arbejdet?',
    'Kan I forklare tilbuddet?',
    'Er min faktura betalt?',
  ],
};

const MODE_LABELS: Record<AgentMode, { title: string; subtitle: string }> = {
  office: { title: 'AI Assistent', subtitle: 'Din personlige assistent' },
  field: { title: 'AI Assistent', subtitle: 'Hjælp til opgaver & vagter' },
  portal: { title: 'Kundeassistent', subtitle: 'Vi hjælper gerne · 24/7' },
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted text-muted-foreground text-[11px]"
      title="Kopiér"
    >
      {copied ? (<><Check className="w-3 h-3 text-green-500" /><span className="text-green-500">Kopieret</span></>) : (<><Copy className="w-3 h-3" /><span>Kopiér</span></>)}
    </button>
  );
}

export function PersonalAgentChat({
  mode = 'office', companyName, extraHeaders, onBack, onClose, embedded,
  compact, initialMessage, onSaveNote, showSaveNote,
}: PersonalAgentChatProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [storageKey, setStorageKey] = useState('personal_agent_history');
  const [open, setOpen] = useState(!!embedded);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<Msg[]>(() => loadStoredMessages('personal_agent_history'));

  // Scope storage key to authenticated user
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const uid = data?.session?.user?.id;
      if (uid) setStorageKey(`personal_agent_history_${uid}`);
    });
  }, []);

  // Reload messages when storageKey becomes user-scoped
  useEffect(() => {
    if (storageKey === 'personal_agent_history') return;
    setMessages(loadStoredMessages(storageKey));
  }, [storageKey]);

  // Keep latest open/embedded in refs so the realtime effect below doesn't re-subscribe on toggle
  const openRef = useRef(open);
  const embeddedRef = useRef(embedded);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { embeddedRef.current = embedded; }, [embedded]);

  // ── Proactive AI messages: fetch unread on mount + realtime subscription ──
  useEffect(() => {
    if (storageKey === 'personal_agent_history') return; // no user yet

    const fetchUnread = async () => {
      const { data: unread } = await supabase
        .from('proactive_ai_messages')
        .select('id, content')
        .eq('read', false)
        .order('created_at', { ascending: true });

      if (unread && unread.length > 0) {
        const newMsgs: Msg[] = unread.map((m) => ({ role: 'assistant' as const, content: m.content }));
        setMessages((prev) => [...prev, ...newMsgs]);
        scrollToBottom();

        // Mark as read
        const ids = unread.map((m) => m.id);
        await supabase
          .from('proactive_ai_messages')
          .update({ read: true })
          .in('id', ids);

        // Show badge instead of auto-opening
        if (!openRef.current && !embeddedRef.current) setUnreadCount(unread.length);
      }
    };

    fetchUnread();

    // Unique topic per mount so a rapid unmount/remount can't reuse a joined channel
    const channel = supabase
      .channel(`proactive-ai-${storageKey}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'proactive_ai_messages',
        },
        async (payload) => {
          const row = payload.new as { id: string; content: string; user_id: string };
          setMessages((prev) => [...prev, { role: 'assistant', content: row.content }]);
          scrollToBottom();

          await supabase
            .from('proactive_ai_messages')
            .update({ read: true })
            .eq('id', row.id);

          if (!openRef.current && !embeddedRef.current) setUnreadCount(c => c + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [storageKey]);
  const briefingSentRef = useRef(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const initialSentRef = useRef(false);

  // Warn before unload while AI is streaming
  useEffect(() => {
    if (!isLoading) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'AI er i gang — er du sikker på at du vil forlade siden?';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isLoading]);

  // Persist to localStorage
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), messages: messages.slice(-50) }));
    }
  }, [messages, storageKey]);

  const scrollToBottom = () => {
    setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  // Auto-scroll to bottom when chat opens or messages load from storage
  useEffect(() => {
    if (open || embedded) {
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'instant' }), 100);
    }
  }, [open, embedded]);

  // ── Daily briefing trigger ──
  useEffect(() => {
    if (briefingSentRef.current) return;
    if (mode === 'portal') return; // no briefings for portal users
    if (!(open || embedded)) return;

    const checkBriefing = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) return;

        const today = new Date().toISOString().slice(0, 10);
        const { data: profile } = await supabase
          .from('profiles')
          .select('last_briefing_date')
          .eq('id', session.user.id)
          .single();

        if ((profile as any)?.last_briefing_date === today) return;

        briefingSentRef.current = true;

        // Send hidden briefing trigger
        const triggerMsg: Msg = { role: 'user', content: '__BRIEFING_TRIGGER__', hidden: true };
        const newMessages = [...messages, triggerMsg];
        setMessages(newMessages);
        streamChat(newMessages);

        // Mark briefing as sent for today
        await supabase
          .from('profiles')
          .update({ last_briefing_date: today } as any)
          .eq('id', session.user.id);
      } catch (e) {
        console.error('Briefing check failed:', e);
      }
    };

    // Small delay to avoid blocking initial render
    const timer = setTimeout(checkBriefing, 500);
    return () => clearTimeout(timer);
  }, [open, embedded, mode]);

  const abortRef = useRef<AbortController | null>(null);

  // Cleanup on unmount / navigation
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const streamChat = useCallback(async (allMessages: Msg[]) => {
    // Abort any in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);

    // Idle timeout: abort only after 90s with NO streamed progress, so long
    // (but actively streaming) agent answers are not killed mid-response.
    let timeout: ReturnType<typeof setTimeout> = setTimeout(() => controller.abort(), 90_000);
    const resetIdleTimeout = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => controller.abort(), 90_000);
    };

    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-personal`;

      // Get the user's access token for authenticated requests
      let accessToken = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      if (mode !== 'portal') {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.access_token) {
          accessToken = sessionData.session.access_token;
        }
      }

      const hdrs: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        ...(extraHeaders || {}),
      };

      if (mode === 'portal') {
        const sessionToken = localStorage.getItem('portal_session');
        if (sessionToken) hdrs['x-portal-session'] = sessionToken;
      }

      const routeProjectId = window.location.pathname.match(/\/office\/cases\/([0-9a-f-]{36})/)?.[1] || null;
      const resp = await fetch(url, {
        method: 'POST',
        headers: hdrs,
        body: JSON.stringify({ messages: allMessages, mode, ui_context: { route_project_id: routeProjectId } }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Ukendt fejl' }));
        if (resp.status === 429) throw new Error('Tjenesten er overbelastet. Prøv igen om lidt.');
        if (resp.status === 402) throw new Error('Tjenesten er midlertidigt utilgængelig.');
        throw new Error(err.error || `Fejl ${resp.status}`);
      }

      if (!resp.body) throw new Error('Ingen stream');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        // Check abort after each chunk
        if (controller.signal.aborted) break;
        // Progress received — reset the idle timeout.
        resetIdleTimeout();
        buffer += decoder.decode(value, { stream: true });

        let newlineIdx: number;
        while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, newlineIdx);
          buffer = buffer.slice(newlineIdx + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantContent } : m);
                }
                return [...prev, { role: 'assistant', content: assistantContent }];
              });
              scrollToBottom();
            }
          } catch { /* partial JSON */ }
        }
      }

      // If stream ended without any assistant content, show fallback
      if (!assistantContent.trim() && !controller.signal.aborted) {
        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Jeg modtog et tomt svar. Prøv venligst igen.' }]);
      }
    } catch (e: any) {
      // Don't show error if request was intentionally aborted (page refresh, navigation, new message)
      if (e.name === 'AbortError' || controller.signal.aborted) {
        // If aborted by timeout (not by user action like new message), show fallback
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && last.content.trim()) return prev;
          return [...prev, { role: 'assistant', content: '⚠️ Svaret tog for lang tid. Prøv igen.' }];
        });
        return;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${e.message || 'Kunne ikke kontakte assistenten'}` }]);
    } finally {
      clearTimeout(timeout);
      setIsLoading(false);
    }
  }, [mode, extraHeaders]);

  const handleSend = useCallback((text?: string) => {
    const msg = (text || input).trim();
    if (!msg || isLoading) return;
    const userMsg: Msg = { role: 'user', content: msg };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    scrollToBottom();
    streamChat(newMessages);
  }, [input, isLoading, messages, streamChat]);

  // Auto-send initialMessage on mount
  useEffect(() => {
    if (initialMessage && !initialSentRef.current) {
      initialSentRef.current = true;
      handleSend(initialMessage);
    }
  }, [initialMessage, handleSend]);

  // Auto-send updated initialMessage when real summary arrives
  useEffect(() => {
    if (
      initialMessage &&
      initialMessage.includes('## Opkaldsresumé') &&
      !isLoading
    ) {
      const userMsg: Msg = { role: 'user', content: initialMessage, hidden: true };
      const newMessages = [...messages, userMsg];
      setMessages(newMessages);
      streamChat(newMessages);
    }
  }, [initialMessage]);

  const handleClear = () => {
    setMessages([]);
    localStorage.removeItem(storageKey);
  };

  const handleSaveNote = async () => {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAssistant || !onSaveNote) return;
    setSavingNote(true);
    try {
      await onSaveNote(lastAssistant.content);
    } finally {
      setSavingNote(false);
    }
  };

  const labels = MODE_LABELS[mode];
  const suggestions = QUICK_SUGGESTIONS[mode];

  // Floating button (only when not embedded)
  if (!open && !embedded) {
    return (
      <button
        onClick={() => { setOpen(true); setUnreadCount(0); }}
        className={cn(
          "fixed z-50 rounded-full shadow-lg hover:scale-105 transition-transform flex items-center justify-center",
          "bg-primary text-primary-foreground",
          isMobile ? "bottom-20 right-4 w-12 h-12" : "bottom-6 right-6 w-14 h-14"
        )}
        title="Åbn AI-assistent"
      >
        <Bot className={cn(isMobile ? "w-5 h-5" : "w-6 h-6")} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-medium">
            {unreadCount}
          </span>
        )}
      </button>
    );
  }

  if (!open && embedded) return null;

  const handleClosePanel = () => {
    if (onClose) onClose();
    else setOpen(false);
  };

  // Panel sizing - when compact (split-panel), fill available space
  const panelClasses = compact
    ? "flex flex-col h-full w-full"
    : isMobile
      ? "fixed inset-0 z-50"
      : cn(
          "fixed bottom-4 right-4 z-50 rounded-xl border border-border shadow-2xl",
          expanded ? "w-[560px]" : "w-[380px]"
        );
  const panelHeight = compact ? undefined : isMobile ? undefined : expanded ? 640 : 500;

  const hasAssistantMsg = messages.some(m => m.role === 'assistant');

  return (
    <div
      className={cn(panelClasses, "bg-background flex flex-col")}
      style={compact ? undefined : isMobile ? undefined : { height: panelHeight, maxHeight: 'calc(100vh - 2rem)' }}
    >
      {/* Header */}
      <div className={cn("flex items-center gap-2 border-b border-border shrink-0 bg-primary/5", compact ? "px-3 py-2" : "px-4 py-3")}>
        {onBack && (
          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={onBack}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
        )}
        <div className={cn("rounded-full bg-primary/10 flex items-center justify-center", compact ? "w-6 h-6" : "w-8 h-8")}>
          <Bot className={cn(compact ? "w-3 h-3" : "w-4 h-4", "text-primary")} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={cn("font-medium text-foreground", compact ? "text-xs" : "text-sm")}>{labels.title}</h3>
          {!compact && <p className="text-[10px] text-muted-foreground">{companyName || labels.subtitle}</p>}
        </div>
        <div className="flex items-center gap-1">
          {!isMobile && !compact && (
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setExpanded(e => !e)}>
              {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </Button>
          )}
          {messages.length > 0 && (
            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={handleClear} title="Ryd samtale">
              <span className="text-[10px] font-bold">✕</span>
            </Button>
          )}
          {!compact && (
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleClosePanel}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className={cn("flex-1", compact ? "px-2 py-2" : "px-3 py-3")}>
        <div className="space-y-3">
          {messages.filter(m => !m.hidden).length === 0 && !initialMessage && (
            <div className="text-center py-6 space-y-3">
              <Sparkles className="w-8 h-8 text-muted-foreground/30 mx-auto" />
              <p className="text-sm font-medium text-foreground">
                {mode === 'portal' ? 'Hej! Hvordan kan jeg hjælpe?' : 'Hej! Hvad kan jeg hjælpe med?'}
              </p>
              <p className="text-xs text-muted-foreground">
                {mode === 'office' && 'Jeg har overblik over sager, tilbud og fakturaer'}
                {mode === 'field' && 'Jeg kan hjælpe med opgaver, vagter og rapportering'}
                {mode === 'portal' && 'Jeg kan svare på spørgsmål om Deres sag, tilbud og fakturaer'}
              </p>
              <div className="flex flex-wrap gap-1.5 justify-center mt-2">
                {suggestions.map(q => (
                  <button
                    key={q}
                    onClick={() => handleSend(q)}
                    className="text-[10px] px-2.5 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors text-foreground"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.filter(m => !m.hidden).map((msg, i) => (
            <div key={i} className={cn('flex group', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div className={cn(
                'relative max-w-[85%] rounded-xl px-3 py-2 text-sm',
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground'
              )}>
                {msg.role === 'assistant' && (
                  <button
                    onClick={() => setMessages(prev => prev.filter((_, idx) => idx !== i + prev.filter((m2, j) => j < i && m2.hidden).length))}
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 rounded-full bg-muted hover:bg-destructive/10 flex items-center justify-center"
                    title="Luk"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
                {msg.role === 'assistant' ? (
                  <>
                    <div className="prose prose-sm dark:prose-invert max-w-none text-[13px] leading-relaxed [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                    {msg.content.includes('Opkaldsresumé') && (
                      <button
                        onClick={() => {
                          sessionStorage.setItem('wizard_prefill_transcript', msg.content);
                          sessionStorage.setItem('wizard_prefill_source', 'call_summary');
                          navigate('/office/cases/new');
                        }}
                        className="w-full flex items-center justify-center gap-1.5 py-1.5 mt-2 rounded-md bg-primary text-primary-foreground text-[11px] font-medium hover:bg-primary/90 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                        Opret sag fra opkald
                      </button>
                    )}
                    <CopyButton text={msg.content} />
                  </>
                ) : (
                  <p className="whitespace-pre-wrap leading-relaxed text-[13px]">{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
            <div className="flex justify-start">
              <div className="bg-secondary rounded-xl px-4 py-3">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      {/* Save note button */}
      {showSaveNote && hasAssistantMsg && !isLoading && onSaveNote && (
        <div className="px-3 pb-1">
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-1.5 text-xs"
            onClick={handleSaveNote}
            disabled={savingNote}
          >
            {savingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Gem som opkaldsnote
          </Button>
        </div>
      )}

      {/* Input */}
      <div className={cn("border-t border-border shrink-0", compact ? "p-2" : "p-3")}>
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Stil et spørgsmål..."
            className="min-h-[36px] max-h-20 text-sm resize-none"
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button size="icon" className="h-9 w-9 shrink-0" onClick={() => handleSend()} disabled={isLoading || !input.trim()}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
